// Copyright ©️ 2026 eVolve MEP, LLC

import { type ShopConfiguration, type WorkCell, useEvolvePatch, useEvolvePost } from '@evolvemep/api-ts';
import { Textarea, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';

import type { ModalRegistryManagedProps } from 'components/Mantine/Modals/createModalRegistry';
import { FormModal, useFormModal } from 'components/Mantine/Modals/FormModal';
import { isNil, isNotNil } from 'helpers/isNotNil';

type EditableFields = Pick<WorkCell, 'workCellName' | 'workCellDescription'>;

type Props = ModalRegistryManagedProps & {
  shopConfiguration: Pick<ShopConfiguration, 'shopConfigurationId' | 'facilityId'>;
  /** The work cell to edit. Omit to create a new work cell. */
  workCell?: WorkCell;
  /** Prefills the name field when creating a new work cell. */
  defaultWorkCellName?: string;
  /** Raise above the default modal layer when opened from within a popover. */
  zIndex?: number;
  /** Called with the created or updated work cell. */
  onWorkCellSaved: (workCell: WorkCell) => void;
};

const EditWorkCellForm = ({
  shopConfiguration,
  workCell,
  defaultWorkCellName,
  onWorkCellSaved,
}: Pick<Props, 'shopConfiguration' | 'workCell' | 'defaultWorkCellName' | 'onWorkCellSaved'>) => {
  const { apiCall: createWorkCellApiCall } = useEvolvePost('shop/workCell');
  const { apiCall: updateWorkCellApiCall } = useEvolvePatch('shop/workCell/:workCellId');
  const form = useForm<EditableFields>({
    initialValues: {
      workCellName: workCell?.workCellName ?? defaultWorkCellName ?? '',
      workCellDescription: workCell?.workCellDescription ?? '',
    },
    validate: {
      workCellName: (v) => !v,
    },
  });

  const onSubmit = async (values: EditableFields) => {
    if (isNil(workCell)) {
      await createWorkCellApiCall({
        ...values,
        shopConfigurationId: shopConfiguration.shopConfigurationId,
        facilityId: shopConfiguration.facilityId,
      }).then(onWorkCellSaved);
    } else {
      await updateWorkCellApiCall(values, { url: `shop/workCell/${workCell.workCellId}` }).then(onWorkCellSaved);
    }
  };

  useFormModal(form, onSubmit);

  return (
    <>
      <TextInput
        label="Work Cell Name"
        required
        autoFocus
        data-autofocus
        {...form.getInputProps('workCellName')}
        maxLength={64}
      />
      <Textarea label="Description" {...form.getInputProps('workCellDescription')} maxLength={255} />
    </>
  );
};

export const CreateEditWorkCellModal = ({
  shopConfiguration,
  workCell,
  defaultWorkCellName,
  onWorkCellSaved,
  ...props
}: Props) => (
  <FormModal
    title={`${isNotNil(workCell) ? 'Edit' : 'Create'} work cell`}
    confirmationText={isNotNil(workCell) ? 'Save' : 'Create'}
    type="submit"
    {...props}
  >
    <EditWorkCellForm
      shopConfiguration={shopConfiguration}
      workCell={workCell}
      defaultWorkCellName={defaultWorkCellName}
      onWorkCellSaved={onWorkCellSaved}
    />
  </FormModal>
);
